/**
 * Local wall-clock date helpers for the calendar module.
 *
 * The household lives in one timezone at a time, so events are stored as UTC instants and
 * every grid/label is computed in the *viewer's* local time. All-day events are anchored
 * at local midnight (see `allDayRange`). Nothing here reaches for Intl beyond the two
 * label formatters at the bottom, which are display-only.
 */
export const MS_PER_MINUTE = 60_000;
export const MS_PER_DAY = 86_400_000;
/** Historical default (the design's original M T W T F S S headers); callers now pass
 * the household's preference explicitly — see the web calendar's weekStartPreference. */
export const WEEK_STARTS_ON = 1;
export function startOfDay(date) {
    const copy = new Date(date);
    copy.setHours(0, 0, 0, 0);
    return copy;
}
export function endOfDay(date) {
    const copy = new Date(date);
    copy.setHours(23, 59, 59, 999);
    return copy;
}
export function addDays(date, days) {
    const copy = new Date(date);
    copy.setDate(copy.getDate() + days);
    return copy;
}
export function addMonths(date, months) {
    const copy = new Date(date.getFullYear(), date.getMonth() + months, 1);
    // Clamp the day so "Jan 31 + 1 month" lands on Feb 28/29 rather than March.
    const lastDay = new Date(copy.getFullYear(), copy.getMonth() + 1, 0).getDate();
    copy.setDate(Math.min(date.getDate(), lastDay));
    copy.setHours(date.getHours(), date.getMinutes(), 0, 0);
    return copy;
}
export function startOfWeek(date, weekStartsOn = WEEK_STARTS_ON) {
    const day = startOfDay(date);
    const offset = (day.getDay() - weekStartsOn + 7) % 7;
    return addDays(day, -offset);
}
export function startOfMonth(date) {
    return new Date(date.getFullYear(), date.getMonth(), 1);
}
/** `YYYY-MM-DD` in local time — the key every view groups occurrences by. */
export function toDateKey(value) {
    const date = value instanceof Date ? value : new Date(value);
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    const day = `${date.getDate()}`.padStart(2, '0');
    return `${date.getFullYear()}-${month}-${day}`;
}
/** Local midnight of a `YYYY-MM-DD` key. */
export function fromDateKey(key) {
    const [year, month, day] = key.split('-').map(Number);
    return new Date(year ?? 1970, (month ?? 1) - 1, day ?? 1);
}
export function isSameDay(a, b) {
    return toDateKey(a) === toDateKey(b);
}
/** The 6×7 (or 5×7) grid of days the month view renders. */
export function monthGridDays(month, weekStartsOn = WEEK_STARTS_ON) {
    const first = startOfWeek(startOfMonth(month), weekStartsOn);
    const last = endOfDay(new Date(month.getFullYear(), month.getMonth() + 1, 0));
    const cells = [];
    for (let cursor = first; cursor <= last || cells.length % 7 !== 0; cursor = addDays(cursor, 1)) {
        cells.push(cursor);
        if (cells.length >= 42)
            break;
    }
    return cells;
}
export function weekDays(date, weekStartsOn = WEEK_STARTS_ON) {
    const first = startOfWeek(date, weekStartsOn);
    return Array.from({ length: 7 }, (_, index) => addDays(first, index));
}
/** All-day events span local midnight → local midnight of the following day. */
export function allDayRange(day, days = 1) {
    const start = startOfDay(day);
    return { start: start.toISOString(), end: addDays(start, days).toISOString() };
}
/** `6:00a` / `11:30p` — the compact form the agenda and month strip use. */
export function formatTime(value) {
    const date = value instanceof Date ? value : new Date(value);
    const hours = date.getHours();
    const minutes = date.getMinutes();
    const suffix = hours < 12 ? 'a' : 'p';
    const hour12 = hours % 12 === 0 ? 12 : hours % 12;
    return `${hour12}:${`${minutes}`.padStart(2, '0')}${suffix}`;
}
/** `45m`, `1h`, `2h 30m` — duration label under the time in an agenda pill. */
export function formatDuration(startIso, endIso) {
    const minutes = Math.max(0, Math.round((Date.parse(endIso) - Date.parse(startIso)) / MS_PER_MINUTE));
    if (minutes < 60)
        return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    const rest = minutes % 60;
    return rest === 0 ? `${hours}h` : `${hours}h ${rest}m`;
}
/** Agenda section rules (design Screen C · 6) — no empty hour rows. */
export function dayPartOf(value) {
    const date = value instanceof Date ? value : new Date(value);
    const hours = date.getHours();
    if (hours < 12)
        return 'morning';
    // 5pm belongs to the afternoon in the design's agenda (Screen C · 6).
    if (hours < 18)
        return 'afternoon';
    return 'evening';
}
export const DAY_PART_LABELS = {
    morning: 'Morning',
    afternoon: 'Afternoon',
    evening: 'Evening',
};
const MONTHS = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
];
const WEEKDAY_NAMES = [
    'Sunday',
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
];
export function monthName(date) {
    return MONTHS[date.getMonth()] ?? '';
}
export function weekdayName(date) {
    return WEEKDAY_NAMES[date.getDay()] ?? '';
}
/** `Jul 30` / `Jul 30, 2025` (the year only when it isn't `reference`'s). */
export function shortDate(date, reference = new Date()) {
    const label = `${monthName(date).slice(0, 3)} ${date.getDate()}`;
    return date.getFullYear() === reference.getFullYear() ? label : `${label}, ${date.getFullYear()}`;
}
//# sourceMappingURL=dates.js.map